import { NavigatorContext } from "common/components/Navigator/Context";
import { navigatorItems } from "common/components/Navigator/items";
import {
  NavigatorGoToJobItem,
  NavigatorGoToOrgItem,
  NavigatorItem,
  NavigatorSelectAccessItem,
} from "common/components/Navigator/types";
import { useAppSelector } from "common/hooks/storeHooks";
import { OrgOverviewRecord } from "common/records/OrgOverview";
import { accessIdsWithoutCurrent } from "common/selectors/access.selectors";
import { jobOverviews } from "common/selectors/job.selectors";
import { orgOverviews } from "common/selectors/org.selectors";
import { List } from "immutable";
import { useContext } from "react";
import { useLocation } from "react-router";

export const useNavigatorItems = (searchKind?: "org" | "job" | "access") => {
  // Hooks
  const { pathname } = useLocation();
  const { availableActions } = useContext(NavigatorContext);

  // Selectors
  const jobs = useAppSelector(jobOverviews);
  const orgs = useAppSelector(orgOverviews);
  const accessIds = useAppSelector((state) => accessIdsWithoutCurrent(state));

  if (searchKind === "org") {
    return orgs.map(
      (org: OrgOverviewRecord) =>
        ({
          kind: "go-to-org",
          label: org.name || "",
          link: `/orgs/${org.id}/info`,
          orgId: org.id,
        }) as NavigatorGoToOrgItem,
    );
  }

  if (searchKind === "access") {
    return accessIds.map(
      (accessId) =>
        ({
          kind: "select-access",
          label: "",
          accessId,
        }) as NavigatorSelectAccessItem,
    );
  }

  if (searchKind === "job") {
    return jobs.map(
      (job) =>
        ({
          kind: "go-to-job",
          label: job.name || "",
          link: `/jobs/${job.id}/info`,
        }) as NavigatorGoToJobItem,
    );
  }

  // /orgs/:orgId/:orgTab
  if (pathname.match(/\/orgs\/[^/]+\/[^/]+/)) {
    return navigatorItems.get("orgTabs")!.concat(navigatorItems.get("orgSettings")!).concat(availableActions);
  }

  // /jobs/:jobId/:jobTab
  if (pathname.match(/\/jobs\/[^/]+\/[^/]+/)) {
    return navigatorItems.get("jobTabs")!.concat(availableActions);
  }

  // /jobs
  if (pathname.match(/\/jobs/)) {
    return navigatorItems
      .get("default", List<NavigatorItem>())
      .concat(
        jobs.map(
          (job) =>
            ({
              kind: "go-to-job",
              label: job.name || "",
              link: `/jobs/${job.id}/info`,
            }) as NavigatorGoToJobItem,
        ),
      )
      .concat(availableActions);
  }

  return navigatorItems.get("default")!.concat(availableActions);
};
